import React, { useEffect, useState } from 'react';
import { motion } from "framer-motion";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faFile,
  faTimes,
  faFilePdf,
  faFileImage,
  faFileWord,
  faFileCsv,
  faDownload,
  faPaperclip,
  faEnvelope,
  faAt,
  faCopy,
  faReply,
  faReplyAll,
  faPaperPlane,
  faChevronDown,
  faChevronUp,
  faWandMagicSparkles,
  faCircleNotch,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { downloadGmailAttachment } from '../../Api';
import { formalizeTranscript } from '../../Api/copilotApi';
import { handleRole } from '../../Helper/handleRoleHelper';
import { sanitizeEmailHtml } from '../../Helper/sanitizeHtml';
import MicButton from '../voice/MicButton';
import { useSelector } from 'react-redux';
import { useDarkMode } from '../../context/DarkModeContext';
import Tooltip from './Tooltip';
import { showSwal } from '../../Helper/ShowAlert';

/** Short viewports: smaller composer min/max so the thread list keeps usable height (720-class laptops). */
const COMPOSER_SHELL_OUTER_EXPANDED_CORE =
  "max-h-[min(46dvh,32rem)] min-h-[12rem] [@media(max-height:720px)]:max-h-[min(36dvh,22rem)] [@media(max-height:720px)]:min-h-[9rem]";

// Helper function to process email HTML and convert relative image URLs to absolute
const processEmailHtml = (html, message, userEmail) => {
  if (!html) return '';

  // Debug: Check if HTML contains data URLs
  const hasDataUrls = html.includes('data:image/');
  if (hasDataUrls) {
    const dataUrlMatches = html.match(/data:image\/[^"'\s>]+/gi);
    console.log(`📷 Found ${dataUrlMatches?.length || 0} data URLs in email HTML:`, dataUrlMatches?.slice(0, 3));
  }

  // Convert relative attachment URLs to absolute URLs
  // Pattern: /api/admin/attachment?messageId=...&attachmentId=...&userEmail=...
  let processedHtml = html.replace(
    /src="(\/api\/[^"]*attachment[^"]*)"/g,
    (match, url) => {
      // If it's already a full URL or data URL, leave it
      if (url.startsWith('http') || url.startsWith('data:')) {
        return match;
      }
      // Convert relative URL to absolute
      const baseUrl = window.location.origin;
      return `src="${baseUrl}${url}"`;
    }
  );

  // Also handle CID references that might have been missed (fallback)
  // This shouldn't be needed if backend processed them correctly, but just in case
  if (processedHtml.includes('cid:') && message?.inlineImages) {
    console.warn('⚠️ Found CID references in HTML but backend should have replaced them:', processedHtml.match(/cid:[^"'\s>]+/gi));
  }

  // Inbound email HTML is attacker-controllable (monitored inboxes). Sanitize
  // AFTER the attachment-src rewrite so a crafted <img src=x onerror=...> can't
  // run in the app origin and steal the JWT from localStorage.
  return sanitizeEmailHtml(processedHtml);
};

/* The bracket strings that are REAL merge placeholders — the signature block
   template bodies carry as literal text, plus the fallbacks the backend used to
   emit for a missing shipper. Kept in step with MERGE_PLACEHOLDER_TOKENS in
   backend/app/services/emailTemplateService.js.

   This must never become a blanket bracket ban: the "missing information" chaser
   deliberately lists bracketed FIELD LABELS for the customer to complete
   ("Package - 1 - [Hs Code], [Country Of Manufacture]"), and blocking those
   would stop that email being sent at all. */
export const MERGE_PLACEHOLDER_TOKENS = [
  "[Shipper Company Name]",
  "[Shipper Contact Person]",
  "[Shipper Email]",
  "[Shipper Phone]",
  "[Shipper Collection Address]",
  "[Shipper Collection Timings]",
  // Historic typo — still sitting in template bodies and in sent mail.
  "[Shipper Collection TImings]",
  "[Shipper Special Collection Requirements]",
  "[Shipper EORI]",
  "[Shipper Customs Special Instruction]",
  "[Shipper Representation Authorization]",
  "[Export Licencing Needs]",
  "[Your Company Name]",
  "[Your Name]",
  "[Your Contact Information]",
];

/* "[quote_due_date N/A]" is built from a template's own key name at render time,
   and an unsubstituted {{token}} is never something an operator typed. */
const NA_PLACEHOLDER_PATTERN = /\[[A-Za-z_][A-Za-z0-9_]{0,60}\s+N\/A\]/g;
const MUSTACHE_PATTERN = /{{\s*[^{}]{1,80}?\s*}}/g;

/** Unresolved merge placeholders left in a draft, de-duplicated. */
export const findUnresolvedPlaceholders = (...parts) => {
  const haystack = parts.filter((p) => typeof p === "string").join("\n");
  if (!haystack) return [];
  const found = MERGE_PLACEHOLDER_TOKENS.filter((token) => haystack.includes(token));
  found.push(...(haystack.match(NA_PLACEHOLDER_PATTERN) || []));
  found.push(...(haystack.match(MUSTACHE_PATTERN) || []));
  return [...new Set(found)];
};

/**
 * Fill the signature block from the logged-in operator. A token we have no value
 * for is LEFT IN PLACE so the send guard names it — better than posting a
 * customer an email signed by nobody.
 */
export const applySenderIdentity = (html, user) => {
  if (typeof html !== "string" || html === "") return html;
  const name = String(user?.name || "").trim();
  const company = String(user?.reg_u_name || "").trim();
  const contact = [user?.email, user?.mobile_no]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" · ");
  const identity = {
    "[Your Company Name]": name ? `${name}${company ? ` (${company})` : ""}` : company,
    "[Your Name]": name || company,
    "[Your Contact Information]": contact,
  };
  return Object.entries(identity).reduce(
    (acc, [token, value]) => (value ? acc.split(token).join(value) : acc),
    html
  );
};

/** Split From header "Name <email>" / "<email>" into display name + address */
const parseFromParts = (fromHeader, fallbackEmail) => {
  const fb = (fallbackEmail || "").trim();
  if (!fromHeader?.trim()) {
    return { displayName: null, address: fb };
  }
  const trimmed = fromHeader.trim();
  const m = trimmed.match(/^(.+?)\s*<([^>]+)>$/);
  if (m) {
    const name = m[1].replace(/^["']|["']$/g, "").trim();
    const addr = (m[2] || "").trim();
    return {
      displayName: name || null,
      address: addr || fb,
    };
  }
  if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
    return {
      displayName: null,
      address: trimmed.slice(1, -1).trim() || fb,
    };
  }
  return { displayName: null, address: trimmed || fb };
};

/** Reply / Reply All — shared for stacked and merged composer toolbars */
function ReplyModeControls({
  replyMode,
  setReplyMode,
  isDarkMode,
  buttonClassName,
  /** Tighter buttons + icons for single-row composer bar */
  compact = false,
}) {
  const resolvedButtonClass =
    buttonClassName ??
    (compact
      ? "px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-colors duration-150 flex items-center gap-0.5"
      : "px-2 py-1 rounded text-xs font-medium transition-colors duration-150 flex items-center gap-1");
  const iconSize = compact ? "9px" : "10px";
  return (
    <>
      <Tooltip content="Reply to sender only" position="top">
        <button
          type="button"
          onClick={() => setReplyMode("reply")}
          className={resolvedButtonClass}
          style={{
            backgroundColor:
              replyMode === "reply"
                ? isDarkMode
                  ? "rgba(0, 168, 255, 0.2)"
                  : "rgba(0, 168, 255, 0.1)"
                : "transparent",
            border: `1px solid ${
              replyMode === "reply"
                ? isDarkMode
                  ? "rgba(0, 168, 255, 0.4)"
                  : "rgba(0, 168, 255, 0.3)"
                : isDarkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)"
            }`,
            color:
              replyMode === "reply"
                ? isDarkMode
                  ? "#60A5FA"
                  : "#00A8FF"
                : isDarkMode
                  ? "rgba(255,255,255,0.65)"
                  : "#3A3A3A",
            fontFamily: "inherit",
            letterSpacing: "0.1px",
          }}
        >
          <FontAwesomeIcon icon={faReply} style={{ fontSize: iconSize }} />
          Reply
        </button>
      </Tooltip>
      <Tooltip content="Reply to sender and all recipients" position="top">
        <button
          type="button"
          onClick={() => setReplyMode("replyAll")}
          className={resolvedButtonClass}
          style={{
            backgroundColor:
              replyMode === "replyAll"
                ? isDarkMode
                  ? "rgba(0, 168, 255, 0.2)"
                  : "rgba(0, 168, 255, 0.1)"
                : "transparent",
            border: `1px solid ${
              replyMode === "replyAll"
                ? isDarkMode
                  ? "rgba(0, 168, 255, 0.4)"
                  : "rgba(0, 168, 255, 0.3)"
                : isDarkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)"
            }`,
            color:
              replyMode === "replyAll"
                ? isDarkMode
                  ? "#60A5FA"
                  : "#00A8FF"
                : isDarkMode
                  ? "rgba(255,255,255,0.65)"
                  : "#3A3A3A",
            fontFamily: "inherit",
            letterSpacing: "0.1px",
          }}
        >
          <FontAwesomeIcon icon={faReplyAll} style={{ fontSize: iconSize }} />
          Reply All
        </button>
      </Tooltip>
    </>
  );
}

/** Collapsed reply bar + hide control; keeps composer subtree mounted (display:none) so drafts/attachments persist. */
function CollapsibleComposerShell({
  collapsibleComposer,
  expanded,
  onSetExpanded,
  replyPlainPreview,
  outerClassWhenExpanded,
  outerClassWhenCollapsed,
  outerStyle,
  /** Optional walkthrough anchor (e.g. qd-reply-composer) */
  dataTour,
  /** When set, replaces the default “Hide reply” row while expanded (e.g. merged Reply + template + ✕). */
  replaceExpandedHeader,
  children,
}) {
  const stripExpanded = !collapsibleComposer || expanded;
  const showCollapsedBar = collapsibleComposer && !expanded;
  const raw = replyPlainPreview || "";
  const draftSnippet = raw.length > 80 ? `${raw.slice(0, 77)}…` : raw;
  const hasDraft = draftSnippet.length > 0;

  return (
    <div
      data-tour={dataTour || undefined}
      className={`box-border min-w-0 max-w-full ${stripExpanded ? outerClassWhenExpanded : outerClassWhenCollapsed}`}
      style={outerStyle}
    >
      {collapsibleComposer && expanded ? (
        replaceExpandedHeader ? (
          replaceExpandedHeader({ onClose: () => onSetExpanded(false) })
        ) : (
          <div className="flex shrink-0 items-center justify-end border-b border-slate-200/80 px-3 py-2 dark:border-[oklch(0.33_0.022_264/0.8)] max-lg:px-2 max-lg:py-1.5">
            <button
              type="button"
              onClick={() => onSetExpanded(false)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264/0.8)] max-lg:px-2 max-lg:py-1 max-lg:text-[11px]"
            >
              Hide reply
              <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "11px" }} />
            </button>
          </div>
        )
      ) : null}
      {showCollapsedBar ? (
        <button
          type="button"
          onClick={() => onSetExpanded(true)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50/90 dark:hover:bg-[oklch(0.26_0.028_264/0.4)] max-[1280px]:gap-2 max-[1280px]:px-3 max-[1280px]:py-2.5 max-lg:px-2.5 max-lg:py-2"
          aria-expanded={false}
        >
          <span className="flex min-w-0 flex-1 items-center gap-3 max-lg:gap-2.5">
            <FontAwesomeIcon
              icon={faReply}
              className="shrink-0 text-fk-accent"
              style={{ fontSize: "12px" }}
              aria-hidden
            />
            <span
              className={`truncate text-sm font-semibold max-lg:text-[13px] max-[960px]:text-[12px] ${
                hasDraft
                  ? "text-slate-800 dark:text-slate-100"
                  : "text-slate-600 dark:text-slate-300"
              }`}
            >
              {hasDraft ? draftSnippet : "Expand to write a reply…"}
            </span>
          </span>
          <FontAwesomeIcon
            icon={faChevronUp}
            className="shrink-0 text-slate-400 dark:text-slate-500"
            style={{ fontSize: "12px" }}
          />
        </button>
      ) : null}
      <div
        className={
          showCollapsedBar
            ? "hidden min-h-0 flex-1 flex-col"
            : "flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col overflow-x-auto overflow-y-hidden"
        }
      >
        {children}
      </div>
    </div>
  );
}

const EmailThread = ({
  messages = [],
  onReplySubmit,
  templateData = '',
  USER_EMAIL,
  /** Quote behind this thread (customer conversations). When set, PDF/image
      attachments get an eye button that pins the file into the Copilot panel
      ("ask about this document"). Leave unset for non-customer threads — the
      Copilot can only read the quote's own customer thread. */
  quoteId,
  threadId,
  order = 'asc',
  bottomRef,
  /** Default “To” address when there is no thread yet (e.g. airline/agent email). */
  toEmail,
  /** When this value changes (e.g. selected airline id), ReplyBox resets “To” sync so the field follows the new partner. */
  recipientSyncKey,
  variant = 'default',
  /** Larger reply/editor strip for transport/airline/shipper panels (message list still flex-1 + scroll) */
  roomyComposer = false,
  /** Email Queries listing: stack messages + reply vertically; page scrolls (no viewport-locked split pane). */
  naturalStackHeight = false,
  /** Rendered directly above the reply composer (e.g. template picker). */
  composerAddon = null,
  /** When this identity changes (e.g. thread / partner), message list scrolls to latest once. */
  conversationScrollKey,
  /** Fires when the reply draft differs from the last template-applied baseline (for template-change confirm). */
  onReplyDraftDirtyChange,
  /** Collapse the reply strip (collapsed by default when true) so the message list gets more vertical space. */
  collapsibleComposer = false,
  /** Walkthrough: spotlight the reply / composer strip (quote detail tour). */
  composerTourId,
  /** Split-pane listings (Email Queries / All Quotes): denser padding + type scale like ~90% browser zoom */
  listingCompact = false,
  /** Per-message assist: (messageId, anchorElement) — opens summary/reply UI; not form-fill */
  onMessageAssist,
  messageAssistInFlight = false,
  /** { text, nonce } — inject suggested reply into reply composer when nonce changes */
  replyDraftInjection,
}) => {
  const [replyText, setReplyText] = useState("");
  const [replyMode, setReplyMode] = useState('reply'); // 'reply' or 'replyAll'
  const { isDarkMode } = useDarkMode();
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const isFlatVariant = variant === 'flat';
  const listingCompactLayout = Boolean(listingCompact);
  const flatListScroll = !isFlatVariant
    ? "space-y-3 px-3 pt-10"
    : listingCompactLayout
      ? "space-y-2 px-2.5 pt-3 sm:px-3 sm:pt-3.5 max-[1280px]:space-y-1.5 max-[1280px]:px-2 max-[1280px]:pt-2.5 max-lg:space-y-1 max-lg:px-1.5 max-lg:pt-2"
      : "space-y-4 px-3 pt-6 sm:px-5 sm:pt-8 max-[1280px]:space-y-3 max-[1280px]:px-2.5 max-[1280px]:pt-4 max-lg:space-y-2 max-lg:px-2 max-lg:pt-3";
  const roomyScrollPad =
    listingCompactLayout && isFlatVariant ? "scroll-pt-5 scroll-pb-3" : "scroll-pt-8 scroll-pb-4";
  /** One row: To + Reply / Reply All / (optional template) / ✕ whenever the composer can collapse — all quote/admin/incomplete threads, flat or not */
  const mergeReplyComposerToolbar = Boolean(collapsibleComposer);
  const normalizeDraftHtml = React.useCallback(
    (html) => (html == null ? "" : String(html)).replace(/\s+/g, " ").trim(),
    []
  );
  const templateBaselineRef = React.useRef("");

  const [replyComposerExpanded, setReplyComposerExpanded] = useState(
    () => !collapsibleComposer
  );
  const prevConversationKeyRef = React.useRef(conversationScrollKey);

  React.useEffect(() => {
    if (!collapsibleComposer) {
      setReplyComposerExpanded(true);
      return;
    }
    if (prevConversationKeyRef.current === conversationScrollKey) return;
    prevConversationKeyRef.current = conversationScrollKey;
    setReplyComposerExpanded(false);
  }, [collapsibleComposer, conversationScrollKey]);

  React.useEffect(() => {
    if (!collapsibleComposer || !replyComposerExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setReplyComposerExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [collapsibleComposer, replyComposerExpanded]);

  const replyPlainPreview = React.useMemo(() => {
    const raw = String(replyText || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return raw;
  }, [replyText]);

  useEffect(() => {
    const raw = templateData?.message;
    if (typeof raw === "string" && raw.trim() !== "") {
      /* Every sender token, everywhere in the body. The old patch was a
         non-global replace of "[Your Company Name]" alone, so a template that
         names it twice signed off with the token still in it, and "[Your Name]"
         / "[Your Contact Information]" were never touched at all — that is what
         customers and airlines read at the bottom of the mail. */
      const applied = applySenderIdentity(raw, userLoggedIn);
      setReplyText(applied);
      templateBaselineRef.current = normalizeDraftHtml(applied);
      onReplyDraftDirtyChange?.(false);
      // Auto-open composer so the freshly-inserted template is visible without an extra click.
      if (collapsibleComposer) setReplyComposerExpanded(true);
    }
  }, [
    templateData?.message,
    userLoggedIn?.name,
    userLoggedIn?.reg_u_name,
    userLoggedIn?.email,
    userLoggedIn?.mobile_no,
    normalizeDraftHtml,
    onReplyDraftDirtyChange,
    collapsibleComposer,
  ]);

  // Existing thread + no template body: keep composer empty (follow-up replies).
  // Depend on messages.length (primitive), not the array — parents like CustomerConversation
  // build combinedMessages with spread on every render, which would otherwise wipe the composer
  // (including a just-injected AI suggested reply) on unrelated re-renders.
  useEffect(() => {
    const raw = templateData?.message;
    const hasThread = (messages?.length || 0) > 0;
    if (!hasThread) return;
    if (typeof raw === "string" && raw.trim() !== "") return;
    // Dirty-draft guard: never wipe a draft the operator is actively editing
    // (a half-typed follow-up or a just-injected AI suggested reply) just
    // because a new inbound message arrived and bumped messages.length.
    const currentIsDirty =
      normalizeDraftHtml(replyText) !== templateBaselineRef.current;
    if (currentIsDirty) return;
    setReplyText("");
    templateBaselineRef.current = "";
    onReplyDraftDirtyChange?.(false);
  }, [
    messages?.length,
    templateData?.message,
    onReplyDraftDirtyChange,
    replyText,
    normalizeDraftHtml,
  ]);

  const handleReplyTextChange = React.useCallback(
    (value) => {
      setReplyText(value);
      const dirty =
        normalizeDraftHtml(value) !==
        normalizeDraftHtml(templateBaselineRef.current);
      onReplyDraftDirtyChange?.(dirty);
    },
    [normalizeDraftHtml, onReplyDraftDirtyChange]
  );

  const escapeHtml = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const prevAssistInjectNonce = React.useRef(0);
  React.useEffect(() => {
    const n = replyDraftInjection?.nonce;
    if (!n || n === prevAssistInjectNonce.current) return;
    prevAssistInjectNonce.current = n;
    const raw = (replyDraftInjection?.text || "").trim();
    if (!raw) return;
    const safe = escapeHtml(raw).replace(/\n/g, "<br/>");
    const block = `<p>${safe}</p>`;
    setReplyText((prev) => {
      const prevTrim = String(prev || "").trim();
      if (!prevTrim) return block;
      return `${prev}<p><br/></p>${block}`;
    });
    setReplyComposerExpanded(true);
    onReplyDraftDirtyChange?.(true);
  }, [replyDraftInjection, onReplyDraftDirtyChange]);

  // ✅ Memoize orderedMessages with deep comparison to prevent unnecessary re-renders
  // Using refs to cache the stringified version and the ordered result
  const messagesStrRef = React.useRef('');
  const orderedMessagesCacheRef = React.useRef([]);

  const orderedMessages = React.useMemo(() => {
    if (!Array.isArray(messages)) return [];

    // Deep comparison: stringify the messages array
    const currentMessagesStr = JSON.stringify(messages);

    // If messages content hasn't changed, return cached result
    if (currentMessagesStr === messagesStrRef.current) {
      return orderedMessagesCacheRef.current;
    }

    // Messages changed, recalculate
    const ordered = order === 'desc' ? [...messages].reverse() : messages;
    messagesStrRef.current = currentMessagesStr;
    orderedMessagesCacheRef.current = ordered;
    return ordered;
  }, [messages, order]);

  // Get the latest message (first in desc order, last in asc order)
  const latestMessage = orderedMessages.length > 0
    ? (order === 'desc' ? orderedMessages[0] : orderedMessages[orderedMessages.length - 1])
    : null;

  // Helper function to detect if current user was BCC'd
  const wasCurrentUserBccd = React.useCallback((message, userEmail) => {
    if (!message || !userEmail) return false;
    const userEmailLower = userEmail.toLowerCase();
    const inTo = (message.to || []).some(email => email && email.toLowerCase() === userEmailLower);
    const inCc = (message.cc || []).some(email => email && email.toLowerCase() === userEmailLower);
    // If user received message but not in To/CC, they were likely BCC'd
    return !inTo && !inCc;
  }, []);

  // Helper function to deduplicate email addresses
  const deduplicateEmails = React.useCallback((emails) => {
    const seen = new Set();
    return emails.filter(email => {
      if (!email) return false;
      const lower = email.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  }, []);

  // Find the message to reply to (if latest is from current user, use previous message)
  const messageToReplyTo = React.useMemo(() => {
    if (!latestMessage || !USER_EMAIL) return null;

    // If latest message is from current user, find the most recent message from someone else
    const latestEmail = latestMessage.email?.toLowerCase();
    const userEmail = USER_EMAIL?.toLowerCase();

    if (latestEmail === userEmail) {
      // Find the most recent message not from current user
      for (const msg of orderedMessages) {
        const msgEmail = msg.email?.toLowerCase();
        if (msgEmail && msgEmail !== userEmail) {
          return msg;
        }
      }
      // If all messages are from current user, use the first one's recipients
      return orderedMessages.length > 0 ? orderedMessages[0] : null;
    }

    return latestMessage;
  }, [orderedMessages, latestMessage, USER_EMAIL]);

  // ✅ Gmail-accurate recipient calculation
  // Implements EXACT Gmail behavior for Reply and Reply All
  const calculateRecipients = React.useCallback(() => {
    if (!messageToReplyTo || !USER_EMAIL) {
      return { to: [], cc: [], bcc: [] };
    }

    // Helper to extract email from "Name <email>" format or return as-is
    const extractEmail = (emailStr) => {
      if (!emailStr) return null;
      const str = emailStr.toString().trim();
      if (str.includes('<')) {
        const match = str.match(/<([^>]+)>/);
        return match ? match[1].trim() : str;
      }
      return str;
    };

    // Helper to normalize email addresses from array or string
    const normalizeEmails = (emails) => {
      if (!emails) return [];
      if (Array.isArray(emails)) {
        return emails.map(extractEmail).filter(Boolean);
      }
      if (typeof emails === 'string') {
        return emails.split(',').map(e => extractEmail(e.trim())).filter(Boolean);
      }
      return [];
    };

    // ✅ CRITICAL: Always exclude current user from To/CC
    const excludeCurrentUser = (emails) => {
      const normalized = normalizeEmails(emails);
      const userEmailLower = USER_EMAIL.toLowerCase().trim();
      return normalized.filter(email => {
        const emailLower = email.toLowerCase().trim();
        return emailLower && emailLower !== userEmailLower;
      });
    };

    const senderEmail = extractEmail(messageToReplyTo.email);
    const senderEmailLower = senderEmail?.toLowerCase().trim();
    const userEmailLower = USER_EMAIL.toLowerCase().trim();

    if (replyMode === 'reply') {
      // ✅ Gmail Reply behavior (Rule 2):
      // To = sender of the message being replied to
      // CC = empty
      // BCC = empty
      // Never auto-include original CC/To lists or current user

      if (senderEmailLower === userEmailLower) {
        // Replying to own message: use original To recipients (excluding self)
        const toRecipients = excludeCurrentUser(messageToReplyTo.to || []);
        return {
          to: toRecipients,
          cc: [],
          bcc: []
        };
      }

      // Normal reply: only sender (excluding current user)
      return {
        to: senderEmail && senderEmailLower !== userEmailLower ? [senderEmail] : [],
        cc: [],
        bcc: []
      };
    } else {
      // ✅ Gmail Reply All behavior (Rule 3):
      // Step 1: To = sender of the message being replied to
      // Step 2: CC = all original To recipients (except you and sender) + all original CC recipients (except you)
      // Step 3: BCC = empty (never auto-fill)
      // Step 4: Deduplicate and remove current user

      // Accumulate all unique recipients from entire thread history
      const allToRecipients = new Set();
      const allCcRecipients = new Set();

      // Process all messages in thread to accumulate recipients
      orderedMessages.forEach(msg => {
        // Add TO recipients (excluding current user)
        const toEmails = excludeCurrentUser(msg.to || []);
        toEmails.forEach(email => {
          const emailLower = email.toLowerCase().trim();
          if (emailLower) {
            allToRecipients.add(emailLower);
          }
        });

        // Add CC recipients (excluding current user)
        const ccEmails = excludeCurrentUser(msg.cc || []);
        ccEmails.forEach(email => {
          const emailLower = email.toLowerCase().trim();
          if (emailLower) {
            allCcRecipients.add(emailLower);
          }
        });

        // ✅ BCC is NEVER included (Rule 4)
      });

      // Step 1: To = sender (excluding current user)
      const toRecipients = senderEmail && senderEmailLower !== userEmailLower ? [senderEmail] : [];

      // Step 2: CC = all To recipients (except sender and current user) + all CC recipients (except current user)
      const ccRecipientsSet = new Set();

      // Add all To recipients except sender (sender is already in To)
      Array.from(allToRecipients).forEach(email => {
        if (email !== senderEmailLower) {
          ccRecipientsSet.add(email);
        }
      });

      // Add all CC recipients
      Array.from(allCcRecipients).forEach(email => {
        ccRecipientsSet.add(email);
      });

      // Convert to array and deduplicate
      const ccRecipients = deduplicateEmails(Array.from(ccRecipientsSet));

      // Special case: If replying to own message
      if (senderEmailLower === userEmailLower) {
        // Use original To recipients as primary To
        const originalToRecipients = excludeCurrentUser(messageToReplyTo.to || []);
        // CC includes all other recipients
        const otherCcRecipients = ccRecipients.filter(email =>
          !originalToRecipients.some(toEmail => toEmail.toLowerCase() === email.toLowerCase())
        );

        return {
          to: originalToRecipients.length > 0 ? originalToRecipients : (toRecipients.length > 0 ? toRecipients : []),
          cc: deduplicateEmails(otherCcRecipients),
          bcc: []
        };
      }

      return {
        to: toRecipients,
        cc: ccRecipients,
        bcc: []
      };
    }
  }, [messageToReplyTo, replyMode, USER_EMAIL, deduplicateEmails, orderedMessages]);

  // ✅ Memoize initialRecipients directly instead of using useState + useEffect
  // This prevents infinite re-render loops by only recalculating when actual dependencies change
  // Rule 7: Returns correct To[], CC[], and BCC (always empty unless manually added)
  const initialRecipients = React.useMemo(() => {
    if (orderedMessages.length === 0 && toEmail && String(toEmail).trim()) {
      return { to: [String(toEmail).trim()], cc: [], bcc: [] };
    }
    // Never address a bounce. calculateRecipients() sets To = sender of the
    // message being replied to, and for a Delivery Status Notification that is
    // mailer-daemon — so a failed send left the operator composing to the
    // daemon, unable to reach the partner they were actually writing to.
    // Fall back to the lane's own recipient, which is the partner's address.
    if (
      isBounceMessage(messageToReplyTo) &&
      toEmail &&
      String(toEmail).trim()
    ) {
      return { to: [String(toEmail).trim()], cc: [], bcc: [] };
    }
    return calculateRecipients();
  }, [
    messageToReplyTo,
    replyMode,
    USER_EMAIL,
    calculateRecipients,
    orderedMessages.length,
    toEmail,
  ]);

  /** Right side of merged composer bar (Reply / template / close); To sits beside this in ReplyBox. */
  const mergedComposerToolbarRow = React.useMemo(() => {
    if (!mergeReplyComposerToolbar) return null;
    return (
      <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
        <div className="flex shrink-0 items-center gap-1">
          {messages.length > 0 && latestMessage ? (
            <ReplyModeControls
              replyMode={replyMode}
              setReplyMode={setReplyMode}
              isDarkMode={isDarkMode}
              compact
            />
          ) : null}
        </div>
        {composerAddon ? (
          <div className="w-[9.25rem] min-w-0 shrink-0 sm:w-40 lg:w-44 [&>div]:!space-y-0 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-col [&>div]:items-stretch [&_label]:sr-only [&_select]:box-border [&_select]:h-8 [&_select]:w-full [&_select]:min-w-0 [&_select]:max-w-full [&_select]:truncate [&_select]:rounded-md [&_select]:border [&_select]:border-slate-200/90 [&_select]:bg-white [&_select]:py-0 [&_select]:pl-2 [&_select]:pr-7 [&_select]:text-left [&_select]:text-[11px] dark:[&_select]:border-slate-600 dark:[&_select]:bg-slate-900/80">
            {composerAddon}
          </div>
        ) : null}
        <Tooltip content="Hide reply composer" position="left">
          <button
            type="button"
            onClick={() => setReplyComposerExpanded(false)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/35 dark:text-red-400 dark:hover:bg-red-500/15"
            aria-label="Hide reply composer"
          >
            <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
          </button>
        </Tooltip>
      </div>
    );
  }, [
    mergeReplyComposerToolbar,
    messages.length,
    latestMessage,
    replyMode,
    isDarkMode,
    composerAddon,
  ]);

  const suppressDefaultComposerHideStrip = mergeReplyComposerToolbar ? () => null : undefined;

  const messagesScrollRef = React.useRef(null);
  const conversationKeyRef = React.useRef(undefined);
  const prevMessageCountRef = React.useRef(0);
  // Tracks whether the user is parked near the "new message" edge (bottom for
  // asc order, top for desc). Updated by a real scroll listener — NOT by our
  // programmatic scroll — so when a new SSE message arrives we can tell
  // whether they were following the conversation or reading older history.
  const isAtNewEdgeRef = React.useRef(true);

  // Update isAtNewEdgeRef whenever the user (or layout) actually scrolls.
  // Programmatic scrolls also fire 'scroll', which is fine — after the scroll
  // lands at the edge, the ref re-asserts true, ready for the next message.
  React.useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const NEW_EDGE_THRESHOLD_PX = 80;
    const measure = () => {
      if (order === "desc") {
        isAtNewEdgeRef.current = el.scrollTop <= NEW_EDGE_THRESHOLD_PX;
      } else {
        isAtNewEdgeRef.current =
          el.scrollHeight - el.scrollTop - el.clientHeight <=
          NEW_EDGE_THRESHOLD_PX;
      }
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    return () => el.removeEventListener("scroll", measure);
  }, [order, orderedMessages.length]);

  // Scroll inside the message list:
  //  - on conversation change (instant — land where the user expects),
  //  - when the user sends (latest is from self),
  //  - when a new message arrives AND the user was near the new-edge
  //    (mirrors WhatsApp / Slack: don't yank the viewport if they've scrolled
  //    up to read older messages).
  React.useEffect(() => {
    const el = messagesScrollRef.current;
    const count = orderedMessages.length;
    if (!el || count === 0) {
      prevMessageCountRef.current = count;
      return;
    }

    const scrollKey =
      conversationScrollKey !== undefined &&
      conversationScrollKey !== null &&
      conversationScrollKey !== ""
        ? String(conversationScrollKey)
        : undefined;

    let shouldScroll = false;
    let smooth = true;
    if (scrollKey !== undefined) {
      if (conversationKeyRef.current !== scrollKey) {
        conversationKeyRef.current = scrollKey;
        shouldScroll = true;
        smooth = false; // jumping into a thread should land instantly
      }
    }

    const last =
      order === "desc" ? orderedMessages[0] : orderedMessages[count - 1];
    const lastFromMe =
      USER_EMAIL &&
      last?.email &&
      String(last.email).toLowerCase() === String(USER_EMAIL).toLowerCase();
    if (
      count > prevMessageCountRef.current &&
      (lastFromMe || isAtNewEdgeRef.current)
    ) {
      shouldScroll = true;
    }
    prevMessageCountRef.current = count;

    if (shouldScroll) {
      requestAnimationFrame(() => {
        const box = messagesScrollRef.current;
        if (!box) return;
        const target = order === "desc" ? 0 : box.scrollHeight;
        if (smooth && typeof box.scrollTo === "function") {
          box.scrollTo({ top: target, behavior: "smooth" });
        } else {
          box.scrollTop = target;
        }
      });
    }
  }, [orderedMessages, conversationScrollKey, order, USER_EMAIL]);

  const replyComposerBodyOnly = (
    <div className="flex min-h-0 max-h-full min-w-0 w-full max-w-full flex-1 flex-col overflow-y-auto px-0.5 sm:px-1 max-lg:px-0">
      <ReplyBox
        roomyLayout={roomyComposer}
        toEmail={toEmail}
        recipientSyncKey={recipientSyncKey}
        mergedToolbar={mergedComposerToolbarRow}
        value={replyText}
        onChange={handleReplyTextChange}
        messagesLength={messages.length}
        onSubmit={async (replyText, attachments, recipients) => {
          if (onReplySubmit) {
            await onReplySubmit(replyText, attachments, recipients);
          }
          setReplyText("");
          templateBaselineRef.current = "";
          onReplyDraftDirtyChange?.(false);
          return Promise.resolve();
        }}
        initialRecipients={initialRecipients}
        isExistingThread={messages.length > 0}
        USER_EMAIL={USER_EMAIL}
        replyMode={replyMode}
        variant={variant}
        listingCompact={listingCompactLayout}
      />
    </div>
  );

  const replyComposerChrome = mergeReplyComposerToolbar ? (
    replyComposerBodyOnly
  ) : (
    <>
      {messages.length > 0 && latestMessage && (
        <div
          className={`flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-1.5 ${
            isFlatVariant
              ? listingCompactLayout
                ? "px-3 pt-2 pb-1.5"
                : "px-5 pt-4 pb-2 max-[1280px]:px-3"
              : "px-2 pt-2 pb-1"
          }`}
        >
          <ReplyModeControls replyMode={replyMode} setReplyMode={setReplyMode} isDarkMode={isDarkMode} />
        </div>
      )}
      {composerAddon ? (
        <div
          className={`w-full min-w-0 max-w-full shrink-0 ${
            isFlatVariant
              ? listingCompactLayout
                ? "px-2.5 pb-1.5 sm:px-3"
                : "px-3 pb-2 sm:px-5"
              : "px-2 pb-2"
          }`}
        >
          {composerAddon}
        </div>
      ) : null}
      {replyComposerBodyOnly}
    </>
  );

  if (naturalStackHeight && roomyComposer) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-3 overflow-hidden">
        <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
          <div
            ref={messagesScrollRef}
            className={`min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto overscroll-y-contain scroll-pb-4 ${flatListScroll}`}
          >
            {orderedMessages.map((msg, index) => (
              <MessageCard
                key={msg.id || index}
                {...msg}
                msg={msg}
                USER_EMAIL={USER_EMAIL}
                quoteId={quoteId}
              threadId={threadId}
                variant={variant}
                compact={listingCompactLayout}
                onMessageAssist={onMessageAssist}
                messageAssistInFlight={messageAssistInFlight}
              />
            ))}
            {bottomRef ? <div ref={bottomRef} /> : null}
          </div>
        </div>
        <CollapsibleComposerShell
          collapsibleComposer={collapsibleComposer}
          expanded={replyComposerExpanded}
          onSetExpanded={setReplyComposerExpanded}
          replyPlainPreview={replyPlainPreview}
          replaceExpandedHeader={suppressDefaultComposerHideStrip}
          dataTour={composerTourId}
          outerClassWhenExpanded={`flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-x-auto overflow-y-hidden ${COMPOSER_SHELL_OUTER_EXPANDED_CORE} rounded-b-[24px] border-t border-slate-200/90 bg-white/85pt-4 pb-2 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)]`}
          outerClassWhenCollapsed="flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-hidden rounded-b-[24px] border-t border-slate-200/90 bg-white/85py-2 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)]"
          outerStyle={{
            backgroundColor: isFlatVariant
              ? (isDarkMode ? "#0F1419" : "#FFFFFF")
              : isDarkMode
                ? "#0f172a"
                : "#ffffff",
            fontFamily: "inherit",
          }}
        >
          {replyComposerChrome}
        </CollapsibleComposerShell>
      </div>
    );
  }

  if (naturalStackHeight) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-3 overflow-hidden">
        <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
          <div
            ref={messagesScrollRef}
            className={`min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto overscroll-y-contain scroll-pb-4 ${flatListScroll}`}
          >
            {orderedMessages.map((msg, index) => (
              <MessageCard
                key={msg.id || index}
                {...msg}
                msg={msg}
                USER_EMAIL={USER_EMAIL}
                quoteId={quoteId}
              threadId={threadId}
                variant={variant}
                compact={listingCompactLayout}
                onMessageAssist={onMessageAssist}
                messageAssistInFlight={messageAssistInFlight}
              />
            ))}
            {bottomRef ? <div ref={bottomRef} /> : null}
          </div>
        </div>
        <CollapsibleComposerShell
          collapsibleComposer={collapsibleComposer}
          expanded={replyComposerExpanded}
          onSetExpanded={setReplyComposerExpanded}
          replyPlainPreview={replyPlainPreview}
          replaceExpandedHeader={suppressDefaultComposerHideStrip}
          dataTour={composerTourId}
          outerClassWhenExpanded={`mt-1 flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-x-auto overflow-y-hidden ${COMPOSER_SHELL_OUTER_EXPANDED_CORE} rounded-b-[24px] border-t border-slate-200/90 bg-white/85pt-4 pb-2 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)]`}
          outerClassWhenCollapsed="mt-1 flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-hidden rounded-b-[24px] border-t border-slate-200/90 bg-white/85py-2 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)]"
          outerStyle={{
            backgroundColor: isFlatVariant
              ? (isDarkMode ? "#0F1419" : "#FFFFFF")
              : isDarkMode
                ? "#0f172a"
                : "#ffffff",
            fontFamily: "inherit",
          }}
        >
          {replyComposerChrome}
        </CollapsibleComposerShell>
      </div>
    );
  }

  if (roomyComposer) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
          <div
            ref={messagesScrollRef}
            className={`min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto overscroll-y-contain ${roomyScrollPad} ${flatListScroll}`}
          >
            {orderedMessages.map((msg, index) => (
              <MessageCard
                key={msg.id || index}
                {...msg}
                msg={msg}
                USER_EMAIL={USER_EMAIL}
                quoteId={quoteId}
              threadId={threadId}
                variant={variant}
                compact={listingCompactLayout}
                onMessageAssist={onMessageAssist}
                messageAssistInFlight={messageAssistInFlight}
              />
            ))}
            {bottomRef ? <div ref={bottomRef} /> : null}
          </div>
        </div>
        <CollapsibleComposerShell
          collapsibleComposer={collapsibleComposer}
          expanded={replyComposerExpanded}
          onSetExpanded={setReplyComposerExpanded}
          replyPlainPreview={replyPlainPreview}
          replaceExpandedHeader={suppressDefaultComposerHideStrip}
          dataTour={composerTourId}
          outerClassWhenExpanded={`flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-x-auto overflow-y-hidden ${COMPOSER_SHELL_OUTER_EXPANDED_CORE} border-t border-slate-200/90 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.08)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.4)]`}
          outerClassWhenCollapsed="flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-hidden border-t border-slate-200/90 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.08)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
          outerStyle={{
            backgroundColor: isFlatVariant
              ? (isDarkMode ? '#0F1419' : '#FFFFFF')
              : (isDarkMode ? '#0f172a' : '#ffffff'),
            fontFamily: 'inherit'
          }}
        >
          {replyComposerChrome}
        </CollapsibleComposerShell>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
        <div
          ref={messagesScrollRef}
          className={`min-h-0 w-full min-w-0 max-w-full flex-1 overflow-y-auto overscroll-y-contain ${roomyScrollPad} ${flatListScroll}`}
        >
          {orderedMessages.map((msg, index) => (
            <MessageCard
              key={msg.id || index}
              {...msg}
              msg={msg}
              USER_EMAIL={USER_EMAIL}
              quoteId={quoteId}
              threadId={threadId}
              variant={variant}
              compact={listingCompactLayout}
              onMessageAssist={onMessageAssist}
              messageAssistInFlight={messageAssistInFlight}
            />
          ))}
          {bottomRef ? <div ref={bottomRef} /> : null}
        </div>
      </div>
      <CollapsibleComposerShell
        collapsibleComposer={collapsibleComposer}
        expanded={replyComposerExpanded}
        onSetExpanded={setReplyComposerExpanded}
        replyPlainPreview={replyPlainPreview}
        replaceExpandedHeader={suppressDefaultComposerHideStrip}
        dataTour={composerTourId}
        outerClassWhenExpanded={`flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-x-auto overflow-y-hidden ${COMPOSER_SHELL_OUTER_EXPANDED_CORE} border-t border-slate-200/90 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.08)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.4)]`}
        outerClassWhenCollapsed="flex w-full min-w-0 max-w-full shrink-0 flex-col overflow-hidden border-t border-slate-200/90 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.08)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
        outerStyle={{
          backgroundColor: isFlatVariant
            ? (isDarkMode ? '#0F1419' : '#FFFFFF')
            : (isDarkMode ? '#0f172a' : '#ffffff'),
          fontFamily: 'inherit'
        }}
      >
        {replyComposerChrome}
      </CollapsibleComposerShell>
    </div>
  );
};

// function convertToRichText(text) {
//   const escapeHTML = (str) => str.replace(/[&<>"']/g, (match) => {
//     const escapeChars = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
//     return escapeChars[match];
//   });
//   let richText = escapeHTML(text)
//     .replace(/\r\n/g, "<br>")
//     .replace(/\*([^\*]+)\*/g, "<strong>$1</strong>");
//   richText = richText.replace(/-\s*<br>/g, "<li>");
//   return `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6;">${richText}</div>`;
// }

/** Some APIs return subject as an object; never render that raw in the UI. */
const formatThreadSubject = (subject) => {
  if (subject == null || subject === "") return "";
  if (typeof subject === "string") return subject;
  if (typeof subject === "object") {
    const cand =
      subject.text ?? subject.subject ?? subject.title ?? subject.name;
    if (typeof cand === "string" && cand.trim()) return cand;
    return "";
  }
  return String(subject);
};

// One unambiguous timestamp format for message headers ("9 Jul 2026, 19:53").
// MUST format from the epoch ms (internalDate) — msg.date is already a locale
// string upstream, and re-parsing en-GB "09/07/2026" swaps day/month silently.
/**
 * A bounce / auto-generated delivery report, not a correspondent.
 *
 * Replying to one addresses the mail daemon, which can never reach anybody —
 * and it is exactly the moment the operator needs to write to the REAL partner
 * (usually to a corrected address). Detected from the sender first, since that
 * is unambiguous, with a subject fallback for relays that use another address.
 */
export function isBounceMessage(msg) {
  if (!msg) return false;
  const from = String(msg.email || msg.sender || "").toLowerCase();
  if (/(^|[<\s])(mailer-daemon|postmaster)@/.test(from)) return true;
  const subject = String(msg.subject || "").toLowerCase();
  return /delivery status notification|undeliverable|mail delivery (failed|subsystem)|returned mail|delivery has failed/.test(
    subject
  );
}

const formatMsgDate = (msg, rawDate) => {
  const ms = Number(msg?.internalDate);
  if (Number.isFinite(ms) && ms > 0) {
    return new Date(ms).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  // No epoch available — show the upstream string untouched rather than risk
  // a wrong re-parse.
  return rawDate || "";
};

const MessageCard = React.memo(
  ({
    sender,
    email,
    date,
    subject,
    html,
    msg,
    USER_EMAIL,
    variant,
    compact = false,
    onMessageAssist,
    messageAssistInFlight = false,
    quoteId,
    threadId,
  }) => {
  const { isDarkMode } = useDarkMode();
  const isFlatVariant = variant === 'flat';
  const subjectLine = formatThreadSubject(subject);
  const { displayName, address: fromAddress } = parseFromParts(sender, email);
  const toList = Array.isArray(msg?.to) ? msg.to.filter(Boolean) : [];
  const ccList = Array.isArray(msg?.cc) ? msg.cc.filter(Boolean) : [];
  const toLine = toList.length ? toList.join(", ") : "";
  const ccLine = ccList.length ? ccList.join(", ") : "";
  const hasTo = Boolean(toLine);
  const fromAddr = (fromAddress || "").trim();
  const fromFallback = (sender || "").trim();
  const hasFromAddress = Boolean(fromAddr || fromFallback);
  const showNameRow =
    displayName &&
    fromAddr &&
    displayName.toLowerCase() !== fromAddr.toLowerCase();
  const operatorEmail = (USER_EMAIL || "").trim().toLowerCase();
  const msgEmailForCompare = (msg?.email || email || "").trim().toLowerCase();
  const isOperatorOwnMessage = Boolean(
    operatorEmail &&
      (msgEmailForCompare === operatorEmail ||
        (fromAddr && fromAddr.toLowerCase() === operatorEmail))
  );
  const labelTag =
    "shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500";
  const cardClass = isFlatVariant
    ? compact
      ? 'rounded-xl border border-slate-200/90 bg-white/85 p-3 shadow-[0_2px_12px_rgba(15,23,42,0.06)] ring-1 ring-slate-900/[0.04] transition-shadow duration-200 hover:shadow-[0_6px_20px_rgba(15,23,42,0.1)] dark:border-[oklch(0.4_0.02_264/0.55)] dark:bg-[oklch(0.225_0.026_264/0.94)] dark:ring-white/[0.06] dark:shadow-[0_2px_16px_rgba(0,0,0,0.4)] dark:hover:shadow-[0_6px_24px_rgba(0,0,0,0.5)]'
      : 'rounded-2xl border border-slate-200/90 bg-white/85 p-4 shadow-[0_2px_16px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.04] transition-shadow duration-200 hover:shadow-[0_8px_28px_rgba(15,23,42,0.12)] dark:border-[oklch(0.4_0.02_264/0.55)] dark:bg-[oklch(0.225_0.026_264/0.94)] dark:ring-white/[0.06] dark:shadow-[0_2px_24px_rgba(0,0,0,0.45)] dark:hover:shadow-[0_8px_32px_rgba(0,0,0,0.55)]'
    : 'rounded-xl border border-slate-200/90 bg-white/85 p-3.5 shadow-sm ring-1 ring-slate-900/[0.03] transition-all duration-200 hover:border-slate-300 hover:shadow-md dark:border-[oklch(0.33_0.022_264/0.85)] dark:bg-[oklch(0.225_0.026_264/0.94)] dark:ring-white/[0.05] dark:hover:border-[oklch(0.4_0.02_264)] dark:hover:shadow-lg dark:hover:shadow-black/40';
  return (
    <div
      className={`${cardClass} min-w-0 max-w-full ${
        isOperatorOwnMessage ? "border-l-[3px] !border-l-blue-500/60" : ""
      }`}
      style={{ fontFamily: 'inherit' }}
    >
      <div className="mb-1.5 flex min-w-0 max-w-full justify-between gap-2 items-start">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: 'rgba(0,168,255,0.15)',
              color: '#00A8FF'
            }}
          >
            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '12px' }} />
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {isOperatorOwnMessage && (
              <span className="shrink-0 rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-600 dark:bg-blue-400/10 dark:text-blue-300">
                You
              </span>
            )}
            {/* Unified neutral pill surface for From/To/Cc — small label tag
                differentiates them, no competing colored backgrounds. */}
            <span
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-2 pr-2.5 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.4)]"
              title={hasFromAddress ? `${showNameRow ? `${displayName} ` : ""}${fromAddr || fromFallback}` : undefined}
            >
              <span className={labelTag}>From</span>
              <span className="min-w-0 truncate text-[11px] font-medium leading-tight text-slate-800 dark:text-slate-100">
                {hasFromAddress ? (
                  showNameRow ? (
                    <>
                      <span className="text-slate-900 dark:text-slate-100">{displayName}</span>
                      <span className="mx-0.5 font-normal text-slate-300 dark:text-slate-600">·</span>
                      <span className="font-normal text-slate-600 dark:text-slate-400">{fromAddr || fromFallback}</span>
                    </>
                  ) : (
                    <span className="break-all">{fromAddr || fromFallback}</span>
                  )
                ) : (
                  <span className="font-normal italic text-slate-400 dark:text-slate-500">Not available</span>
                )}
              </span>
            </span>
            {hasTo ? (
              <span
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-2 pr-2.5 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.4)]"
                title={toLine}
              >
                <span className={labelTag}>To</span>
                <span className="min-w-0 max-w-[min(100%,14rem)] truncate text-[11px] leading-tight text-slate-700 dark:text-slate-200">
                  {toLine}
                </span>
              </span>
            ) : null}
            {ccLine ? (
              <span
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-2 pr-2.5 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.4)]"
                title={ccLine}
              >
                <span className={labelTag}>Cc</span>
                <span className="min-w-0 max-w-[min(100%,14rem)] truncate text-[11px] leading-tight text-slate-700 dark:text-slate-200">
                  {ccLine}
                </span>
              </span>
            ) : null}
            {typeof onMessageAssist === "function" &&
            (msg?.id || msg?.messageId) &&
            !isOperatorOwnMessage ? (
              <button
                type="button"
                data-fk-ai-assist-anchor="1"
                title="AI summary and suggested reply for this message"
                disabled={messageAssistInFlight}
                onClick={(e) =>
                  onMessageAssist(msg.id || msg.messageId, e.currentTarget)
                }
                className={`group relative inline-flex shrink-0 items-center gap-1 overflow-hidden rounded-full bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_50%,#3b82f6_100%)] px-2 py-0.5 text-[10px] font-semibold text-white shadow-[0_2px_8px_-3px_rgba(124,58,237,0.4)] transition-all hover:shadow-[0_4px_12px_-3px_rgba(124,58,237,0.55)] hover:brightness-110 disabled:cursor-not-allowed disabled:bg-none disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none dark:disabled:bg-[oklch(0.3_0.024_264)] dark:disabled:text-slate-500 ${
                  compact ? "max-lg:px-1.5" : ""
                }`}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)] transition-transform duration-700 group-hover:translate-x-full group-disabled:hidden"
                />
                <motion.span
                  className="relative inline-flex"
                  animate={
                    messageAssistInFlight
                      ? false
                      : { rotate: [0, -6, 0, 6, 0] }
                  }
                  transition={
                    messageAssistInFlight
                      ? {}
                      : { repeat: Infinity, duration: 5, ease: "easeInOut" }
                  }
                  whileHover={
                    messageAssistInFlight
                      ? undefined
                      : { rotate: [0, -14, 14, -10, 0], transition: { duration: 0.45 } }
                  }
                >
                  <FontAwesomeIcon
                    icon={messageAssistInFlight ? faCircleNotch : faWandMagicSparkles}
                    className={`text-[9px] ${messageAssistInFlight ? "animate-spin" : ""}`}
                    aria-hidden
                  />
                </motion.span>
                <span className="relative">
                  {messageAssistInFlight ? "Working…" : "AI assist"}
                </span>
              </button>
            ) : null}
          </div>
        </div>
        <div
          className="flex-shrink-0 pt-0.5 text-right text-[10px] leading-tight text-slate-500 dark:text-slate-400"
          style={{ fontFamily: 'inherit', maxWidth: '6.5rem' }}
        >
          {formatMsgDate(msg, date)}
        </div>
      </div>

      {subjectLine ? (
        <div
          className="text-xs font-medium mb-1"
          style={{
            color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
            fontFamily: 'inherit',
            letterSpacing: '0.1px',
            fontWeight: 600,
            lineHeight: '1.3'
          }}
        >
          {subjectLine}
        </div>
      ) : null}

      <div
        className={`email-thread-html-body mt-1 max-w-none overflow-x-auto break-words dark:prose-invert [&_img]:!max-w-full [&_img]:!h-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto ${
          isFlatVariant && compact
            ? "text-[11px] prose prose-sm leading-snug pt-1.5 prose-p:my-1"
            : "text-xs prose prose-sm pt-2 leading-relaxed prose-p:my-2"
        }`}
        style={{
          color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
          fontFamily: 'inherit',
          letterSpacing: '0.1px',
          lineHeight: '1.5'
        }}
        dangerouslySetInnerHTML={{ __html: processEmailHtml(html, msg, USER_EMAIL) }}
      />
      {msg.attachments && msg.attachments.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {msg.attachments.map((att, i) => (
            <AttachmentPreview key={i} attachment={att} messageId={msg.id || msg.messageId} USER_EMAIL={USER_EMAIL} quoteId={quoteId} threadId={threadId} />
          ))}
        </div>
      ) : null}
    </div>
  );
});

const AttachmentPreview = ({ attachment, messageId, USER_EMAIL, quoteId, threadId }) => {
  const { filename, mimeType, size, attachmentId } = attachment;
  // Eye = "ask Copilot about this file": pins the attachment into the Copilot
  // panel. Only for types the AI can actually read, on customer threads.
  const canAskCopilot =
    !!quoteId && !!filename && /pdf|image/i.test(mimeType || "");
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const { isDarkMode } = useDarkMode();
  const handleDownload = async () => {
    try {
      const blob = await downloadGmailAttachment({
        token: loginToken,
        messageId,
        attachmentId,
        userEmail: USER_EMAIL,
        role: userLoggedIn?.role,
      });

      // Check if response is actually an error JSON (when API returns error instead of blob)
      if (blob.type === "application/json") {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          if (errorData.error) {
            const errorMessage = errorData.message || "Failed to download attachment";
            const error = errorData.error || "Unknown error";
            const status = errorData.status || 500;
            showSwal(errorMessage, error, status);
            console.error("❌ Attachment download error:", errorData);
            return;
          }
        } catch (parseError) {
          // If it's not JSON, continue with blob handling
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "attachment";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Show success message
      showSwal("Download Started", `Downloading ${filename || "attachment"}`, 200);
    } catch (err) {
      console.error("❌ Error downloading attachment:", err);

      // Extract error message from response
      let errorMessage = "Failed to download attachment";
      let error = "Unknown error";
      let status = 500;

      if (err.response?.data) {
        try {
          // If response is JSON error
          if (typeof err.response.data === 'object') {
            errorMessage = err.response.data.message || errorMessage;
            error = err.response.data.error || error;
            status = err.response.data.status || err.response.status || 500;
          } else if (typeof err.response.data === 'string') {
            const parsed = JSON.parse(err.response.data);
            errorMessage = parsed.message || errorMessage;
            error = parsed.error || error;
            status = parsed.status || err.response.status || 500;
          }
        } catch (parseError) {
          // If parsing fails, use default message
          status = err.response?.status || 500;
        }
      } else if (err.message) {
        errorMessage = err.message;
        if (err.message.includes("timeout")) {
          status = 504;
          error = "Request timed out";
        }
      }

      showSwal(errorMessage, error, status);
    }
  };

  const getFileIcon = () => {
    if (mimeType.includes("pdf")) return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#EF4444', fontSize: '18px' }} />;
    if (mimeType.includes("image")) return <FontAwesomeIcon icon={faFileImage} style={{ color: '#00A8FF', fontSize: '18px' }} />;
    if (mimeType.includes("word")) return <FontAwesomeIcon icon={faFileWord} style={{ color: '#2563EB', fontSize: '18px' }} />;
    if (mimeType.includes("csv")) return <FontAwesomeIcon icon={faFileCsv} style={{ color: '#10B981', fontSize: '18px' }} />;
    return <FontAwesomeIcon icon={faFile} style={{ color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A', fontSize: '18px' }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <Tooltip content={`Download ${filename}`} position="top">
      <div
        onClick={handleDownload}
        className="flex items-center gap-1.5 rounded px-2 py-1 cursor-pointer transition-colors duration-150"
        style={{
          backgroundColor: isDarkMode ? 'rgba(37, 41, 50, 0.3)' : 'rgba(249, 250, 251, 0.8)',
          border: '1px solid rgba(255,255,255,0.25)',
          fontFamily: 'inherit'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37, 41, 50, 0.4)' : 'rgba(249, 250, 251, 0.9)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37, 41, 50, 0.3)' : 'rgba(249, 250, 251, 0.8)';
        }}
      >
        {getFileIcon()}
        <div className="flex flex-col">
          <span
            className="text-xs font-medium"
            style={{
              color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
              fontFamily: 'inherit',
              letterSpacing: '0.1px',
              lineHeight: '1.2'
            }}
          >
            {filename}
          </span>
          <span
            className="text-[10px]"
            style={{
              color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
              fontFamily: 'inherit',
              lineHeight: '1.1'
            }}
          >
            {formatFileSize(size)}
          </span>
        </div>
        <span className="ml-auto flex items-center gap-1.5">
          {canAskCopilot && (
            <button
              type="button"
              title={`Parse ${filename} into this quote`}
              aria-label={`Parse ${filename} into this quote`}
              onClick={(e) => {
                e.stopPropagation();
                /* Reads the document into the quote's container fields — the
                   answer to "cargo details attached", where the body carries
                   nothing and the packing list carries everything. Same
                   decoupled event pattern as the Copilot action beside it, so
                   the handler need not be drilled through the thread. */
                window.dispatchEvent(
                  new CustomEvent("fk:parse-attachment", {
                    detail: { quoteId, filename, threadId },
                  })
                );
              }}
              className="rounded p-0.5 transition-colors hover:text-blue-600 dark:hover:text-blue-300"
              style={{
                color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
              }}
            >
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                style={{ fontSize: '11px' }}
              />
            </button>
          )}
          {canAskCopilot && (
            <button
              type="button"
              title="Ask Copilot about this file"
              aria-label={`Ask Copilot about ${filename}`}
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(
                  new CustomEvent("fk:copilot-ask-doc", {
                    // threadId: the CONVERSATION this file lives on (airline /
                    // transport threads differ from the customer thread) — the
                    // copilot's analyzeAttachment must search THIS thread.
                    detail: { quoteId, filename, threadId },
                  })
                );
              }}
              className="rounded p-0.5 transition-colors hover:text-violet-600 dark:hover:text-violet-300"
              style={{
                color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
              }}
            >
              <FontAwesomeIcon icon={faEye} style={{ fontSize: '11px' }} />
            </button>
          )}
          <FontAwesomeIcon
            icon={faDownload}
            style={{
              color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
              fontSize: '10px'
            }}
          />
        </span>
      </div>
    </Tooltip>
  );
};


const FilePreview = ({ file, onRemove }) => {
  const { isDarkMode } = useDarkMode();
  const isImage = file?.type?.startsWith('image/');
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!isImage || !file) {
      setImageUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <FontAwesomeIcon icon={faFileImage} style={{ color: '#00A8FF', fontSize: '18px' }} />;
    if (type === 'application/pdf') return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#EF4444', fontSize: '18px' }} />;
    if (type.includes('word') || type.includes('document')) return <FontAwesomeIcon icon={faFileWord} style={{ color: '#2563EB', fontSize: '18px' }} />;
    if (type === 'text/csv' || type.includes('spreadsheet')) return <FontAwesomeIcon icon={faFileCsv} style={{ color: '#10B981', fontSize: '18px' }} />;
    return <FontAwesomeIcon icon={faFile} style={{ color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A', fontSize: '18px' }} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div
      className="flex items-center gap-2 p-1.5 rounded transition-colors duration-150"
      style={{
        backgroundColor: isDarkMode ? 'rgba(37, 41, 50, 0.3)' : 'rgba(249, 250, 251, 0.8)',
        border: '1px solid rgba(255,255,255,0.25)',
        fontFamily: 'inherit'
      }}
    >
      <div className="flex-shrink-0">
        {isImage && imageUrl ? (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-9 w-9 overflow-hidden rounded border border-slate-200 dark:border-[oklch(0.33_0.022_264)]"
            title="Click to view full size"
          >
            <img
              src={imageUrl}
              alt={file.name}
              className="h-full w-full object-cover"
            />
          </a>
        ) : (
          getFileIcon(file.type)
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-medium truncate"
          style={{
            color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
            fontFamily: 'inherit',
            letterSpacing: '0.1px',
            lineHeight: '1.3'
          }}
        >
          {file.name}
        </div>
        <div
          className="text-xs"
          style={{
            color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
            fontFamily: 'inherit',
            fontSize: '10px',
            lineHeight: '1.2'
          }}
        >
          {formatFileSize(file.size)}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="flex-shrink-0 p-0.5 rounded transition-colors duration-150"
        style={{
          color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
          backgroundColor: 'rgba(239, 68, 68, 0.1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
        }}
      >
        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '10px' }} />
      </button>
    </div>
  );
};

export const ReplyBox = ({
  value,
  onChange,
  onSubmit,
  buttonText = "Send Reply",
  threadId,
  isInitialSubmitType,
  messagesLength,
  initialRecipients = { to: [], cc: [], bcc: [] },
  isExistingThread = false,
  USER_EMAIL,
  replyMode = 'reply',
  /** Default recipient email string (for new threads / enabling send when To is synced). */
  toEmail,
  recipientSyncKey,
  variant = 'default',
  roomyLayout = false,
  listingCompact = false,
  /** When set (flat collapsible + template), toolbar row is rendered here below an inline To row. */
  mergedToolbar = null,
}) => {
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = React.useRef(null);
  const { isDarkMode } = useDarkMode();
  const [isSending, setIsSending] = useState(false);

  // ── Voice dictation → AI-formalized draft ──────────────────────────────
  const voiceToken = useSelector((state) => state?.reducer?.userToken);
  const voiceRole = useSelector((state) => state?.reducer?.userLoggedIn?.role);
  // Transcribe/formalize endpoints exist only on admin/operator routers, and
  // this composer also renders on public/user pages — gate the mic to avoid a
  // visible button that always 404s.
  const canDictate =
    Boolean(voiceToken) &&
    ["admin", "superAdmin", "operator"].includes(voiceRole || "");
  const [voicePhase, setVoicePhase] = useState("idle"); // idle | formalizing
  const [voiceError, setVoiceError] = useState("");
  const voiceMountedRef = React.useRef(true);
  useEffect(() => () => { voiceMountedRef.current = false; }, []);
  // After an insert: { raw, formalHtml, rawHtml } so the operator can swap to
  // the literal transcript if the rewrite dropped their phrasing.
  const [lastDictation, setLastDictation] = useState(null);

  // Plain transcript → Quill-safe paragraph HTML.
  const dictationToHtml = (text) => {
    const esc = (s) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    return String(text || "")
      .split(/\n{2,}/)
      .map((p) => `<p>${esc(p.trim()).replace(/\n/g, "<br/>")}</p>`)
      .join("");
  };

  const insertDictationHtml = (html) => {
    const base = String(value || "").trim();
    const emptyish = !base || base === "<p><br></p>" || base === "<p></p>";
    onChange(emptyish ? html : `${value}${html}`);
  };

  const showVoiceError = (message) => {
    setVoiceError(message);
    setTimeout(() => setVoiceError(""), 5000);
  };

  const handleDictationTranscript = async (raw) => {
    setVoicePhase("formalizing");
    setVoiceError("");
    try {
      const { formalized } = await formalizeTranscript({
        token: voiceToken,
        role: handleRole(voiceRole),
        transcript: raw,
      });
      // The composer may have unmounted or the reply been sent while we waited.
      if (!voiceMountedRef.current) return;
      const formalHtml = dictationToHtml(formalized);
      insertDictationHtml(formalHtml);
      setLastDictation({ raw, formalHtml, rawHtml: dictationToHtml(raw) });
    } catch (err) {
      if (!voiceMountedRef.current) return;
      // Formalize failed — fall back to inserting the raw transcript so the
      // operator's words are never lost.
      const rawHtml = dictationToHtml(raw);
      insertDictationHtml(rawHtml);
      setLastDictation(null);
      showVoiceError(
        err?.message
          ? `Inserted raw transcript (AI polish failed: ${err.message})`
          : "Inserted raw transcript (AI polish failed)."
      );
    } finally {
      if (voiceMountedRef.current) setVoicePhase("idle");
    }
  };

  const swapToRawDictation = () => {
    if (!lastDictation) return;
    // Only swap when the formalized block is still present verbatim. Once the
    // operator edits the draft, Quill re-normalizes the HTML and an exact match
    // would fail — appending the raw text would duplicate content, so we tell
    // them instead of silently doubling it.
    if (String(value || "").includes(lastDictation.formalHtml)) {
      onChange(value.replace(lastDictation.formalHtml, lastDictation.rawHtml));
      setLastDictation(null);
    } else {
      showVoiceError("Can't swap after edits — undo (Ctrl+Z) to revert instead.");
      setLastDictation(null);
    }
  };
  // ────────────────────────────────────────────────────────────────────────
  const isFlatVariant = variant === 'flat';
  const replyCompact = Boolean(listingCompact && isFlatVariant);
  const [recipients, setRecipients] = useState({
    to: initialRecipients?.to || [],
    cc: initialRecipients?.cc || [],
    bcc: initialRecipients?.bcc || [],
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // ✅ Track manual user actions with refs (synchronous updates)
  const hasManuallyToggledCcRef = React.useRef(false);
  const hasManuallyToggledBccRef = React.useRef(false);
  const hasManuallyEditedToRef = React.useRef(false);
  const hasManuallyEditedCcRef = React.useRef(false);
  const hasManuallyEditedBccRef = React.useRef(false);

  // Switching airline/agent/shipper: allow To to follow the new default without treating it as a manual edit
  useEffect(() => {
    hasManuallyEditedToRef.current = false;
  }, [recipientSyncKey]);

  // Update recipients when initialRecipients or replyMode changes (new threads and existing threads).
  // Guard each field with its "manually edited" ref so the reply-all recompute
  // never clobbers what the operator typed into To / Cc / Bcc. bcc is preserved
  // (not force-blanked) once the operator has edited it.
  useEffect(() => {
    if (!initialRecipients) return;
    setRecipients((prev) => ({
      to: hasManuallyEditedToRef.current ? prev.to : (initialRecipients.to || []),
      cc: hasManuallyEditedCcRef.current ? prev.cc : (initialRecipients.cc || []),
      bcc: hasManuallyEditedBccRef.current ? prev.bcc : (initialRecipients.bcc || []),
    }));
    // For the Cc row: if the operator hasn't touched it, auto-reveal when
    // reply-all produced a non-empty Cc (so the recipients are visible/editable),
    // otherwise keep it collapsed. Bcc stays collapsed until manually opened.
    if (!hasManuallyToggledCcRef.current && !hasManuallyEditedCcRef.current) {
      setShowCc((initialRecipients.cc || []).some((e) => e && String(e).trim()));
    }
    if (!hasManuallyToggledBccRef.current && !hasManuallyEditedBccRef.current) {
      setShowBcc(false);
    }
  }, [initialRecipients, replyMode, isExistingThread]);

  // const navigate = useNavigate();
  // const location = useLocation();

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      [{ 'table': [] }],
      ['clean'],
    ],
    // table:true
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Content = e.target.result.split(',')[1];
        setAttachments(prev => [...prev, {
          file,
          filename: file.name,
          mimeType: file.type,
          contentBase64: base64Content
        }]);
      };
      reader.readAsDataURL(file);
    });
    event.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (isSending) return; // Prevent multiple submissions
    // Don't send while a dictation is still being formalized — the rewrite is
    // about to be inserted, and sending now would drop it or send a stale draft.
    if (voicePhase === "formalizing") {
      showVoiceError("Hang on — finishing your dictation…");
      return;
    }

    // Hard gate against empty sends. The Send Reply button is also disabled
    // via isButtonEnabled, but enter-key / programmatic paths could still
    // fire this — keep it correct here too.
    const plainText = String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&#160;/g, " ")
      .replace(/\s+/g, "")
      .trim();
    if (plainText.length === 0 && attachments.length === 0) {
      return;
    }

    /* Refuse to put an unfilled merge placeholder in front of a customer,
       shipper or airline — this is the composer every conversation pane sends
       through, including the shipper "Initiate Conversation" path that feeds a
       raw template body straight in. Only the known registry is matched, so the
       "missing information" chaser (which lists bracketed field labels on
       purpose) still sends. */
    const unresolved = findUnresolvedPlaceholders(value);
    if (unresolved.length > 0) {
      showSwal(
        "Unfilled details in this email",
        `Not sent — fill in or remove ${unresolved.join(", ")} first.`,
        422
      );
      return;
    }

    setIsSending(true);
    try {
      const attachmentData = attachments.map(({ filename, mimeType, contentBase64 }) => ({
        filename,
        mimeType,
        contentBase64,
      }));

      // Ensure recipients are arrays and trimmed. Cc is included for Reply All
      // (auto-calculated) plus any manual edits; Bcc carries the operator's
      // manual entries. The backend safely handles/validates these.
      const cleanList = (v) =>
        (Array.isArray(v) ? v : v ? [v] : [])
          .map((e) => (e == null ? "" : String(e).trim()))
          .filter(Boolean);
      const normalizedRecipients = {
        to: cleanList(recipients.to),
        cc: cleanList(recipients.cc),
        bcc: cleanList(recipients.bcc),
      };

      if (onSubmit) {
        // Call onSubmit and await the result
        // onSubmit is always async and returns a promise
        await onSubmit(value, attachmentData, normalizedRecipients);
      }
      setAttachments([]);
    } catch (error) {
      console.error('Error sending reply:', error);
      // Don't clear attachments on error so user can retry
    } finally {
      setIsSending(false);
    }
  };

  const isButtonEnabled = React.useMemo(() => {
    // Disable button while sending
    if (isSending) return false;

    // Refuse empty sends: require either typed content (Quill HTML stripped
    // of tags + non-breaking spaces) OR at least one file attachment.
    // Previously the button was enabled purely on thread/recipient state,
    // so an empty reply could fire a blank email.
    const plainText = String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&#160;/g, " ")
      .replace(/\s+/g, "")
      .trim();
    const hasContent = plainText.length > 0 || attachments.length > 0;
    if (!hasContent) return false;

    // For existing threads: enable if there are messages
    if (messagesLength > 0) return true;

    const hasValidRecipient =
      recipients.to &&
      recipients.to.length > 0 &&
      recipients.to.some((email) => email && String(email).trim());

    // For initial submit: need a real To line (synced from partner email or typed)
    if (isInitialSubmitType) {
      return hasValidRecipient;
    }

    if (typeof toEmail === "string" && toEmail.trim() && hasValidRecipient) {
      return true;
    }

    return !!(threadId && isInitialSubmitType);
  }, [messagesLength, isInitialSubmitType, recipients.to, threadId, isSending, toEmail, value, attachments.length]);

  useEffect(() => {
    const style = document.createElement('style');
    const editorWrapMaxH = roomyLayout
      ? 'min(42dvh, 30rem)'
      : isFlatVariant
        ? 'min(38dvh, 26rem)'
        : 'min(34dvh, 22rem)';
    /* Scoped so only this reply editor is affected; max-height + flex keeps Send visible */
    style.innerHTML = `
      .email-thread-reply-editor-wrap {
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        min-width: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        min-height: ${roomyLayout ? '12rem' : '7rem'} !important;
        max-height: ${editorWrapMaxH} !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
      }
      .email-thread-reply-editor-wrap .quill {
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 0% !important;
        min-width: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        min-height: ${roomyLayout ? '11rem' : '7rem'} !important;
        height: 100% !important;
        max-height: 100% !important;
        overflow: hidden !important;
      }
      .email-thread-reply-editor-wrap .ql-toolbar .ql-formats {
        display: inline-flex !important;
        flex-wrap: wrap !important;
        align-items: center !important;
        row-gap: 4px !important;
        column-gap: 2px !important;
        max-width: 100% !important;
        vertical-align: middle !important;
      }
      .email-thread-reply-editor-wrap .ql-toolbar {
        flex-shrink: 0 !important;
        flex-wrap: wrap !important;
        row-gap: 6px !important;
        column-gap: 2px !important;
        max-width: 100% !important;
        width: 100% !important;
        box-sizing: border-box !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        background-color: ${isDarkMode ? 'rgba(37, 41, 50, 0.4)' : 'rgba(249, 250, 251, 0.8)'} !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} !important;
        border-bottom: none !important;
        border-radius: 6px 6px 0 0 !important;
      }
      .email-thread-reply-editor-wrap .ql-toolbar .ql-stroke {
        stroke: ${isDarkMode ? '#E5E7EB' : '#1E1E1E'} !important;
      }
      .email-thread-reply-editor-wrap .ql-toolbar .ql-fill {
        fill: ${isDarkMode ? '#E5E7EB' : '#1E1E1E'} !important;
      }
      .email-thread-reply-editor-wrap .ql-toolbar button:hover {
        background-color: ${isDarkMode ? 'rgba(0,168,255,0.1)' : 'rgba(0,168,255,0.05)'} !important;
      }
      .email-thread-reply-editor-wrap .ql-container {
        display: flex !important;
        flex-direction: column !important;
        border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} !important;
        border-radius: 0 0 6px 6px !important;
        background-color: ${isDarkMode ? 'rgba(37, 41, 50, 0.3)' : 'rgba(255,255,255,0.8)'} !important;
        flex: 1 1 0% !important;
        min-width: 0 !important;
        max-width: 100% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        overflow: hidden !important;
      }
      .email-thread-reply-editor-wrap .ql-container:focus-within {
        border-color: ${isDarkMode ? 'rgba(0, 168, 255, 0.5)' : 'rgba(0, 168, 255, 0.4)'} !important;
        box-shadow: 0 0 0 2px ${isDarkMode ? 'rgba(0, 168, 255, 0.15)' : 'rgba(0, 168, 255, 0.1)'} !important;
      }
      .email-thread-reply-editor-wrap .ql-editor {
        color: ${isDarkMode ? '#E5E7EB' : '#1E1E1E'} !important;
        background-color: transparent !important;
        backdrop-filter: blur(8px) !important;
        -webkit-backdrop-filter: blur(8px) !important;
        border: none !important;
        border-radius: 0 0 6px 6px !important;
        font-family: Inter, Manrope, Poppins, sans-serif !important;
        flex: 1 1 0% !important;
        min-height: ${roomyLayout ? '168px' : isFlatVariant ? '88px' : '104px'} !important;
        max-height: 100% !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        overscroll-behavior: contain !important;
      }
      @media (max-height: 720px) {
        .email-thread-reply-editor-wrap {
          min-height: ${roomyLayout ? '9rem' : '6rem'} !important;
          max-height: ${roomyLayout ? 'min(32dvh, 22rem)' : isFlatVariant ? 'min(28dvh, 18rem)' : 'min(26dvh, 16rem)'} !important;
        }
        .email-thread-reply-editor-wrap .quill {
          min-height: ${roomyLayout ? '8.5rem' : '6rem'} !important;
        }
        .email-thread-reply-editor-wrap .ql-editor {
          min-height: ${roomyLayout ? '120px' : isFlatVariant ? '72px' : '88px'} !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [isDarkMode, isFlatVariant, roomyLayout]);

  const documentFlowReply = isFlatVariant && !roomyLayout;

  return (
    <div
      className={`flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden transition-colors duration-150 box-border ${isFlatVariant ? (replyCompact ? 'p-2.5 pb-2 sm:px-3 max-[960px]:px-2' : 'p-4 pb-3 max-[1280px]:p-3 max-[1280px]:pb-2.5 max-lg:p-2.5 max-lg:pb-2 max-[960px]:px-2') : 'p-2'}`}
      style={{
        fontFamily: 'inherit'
      }}
    >
      <div className="flex min-h-0 min-w-0 max-w-full w-full flex-1 flex-col overflow-hidden">
      <div className={`shrink-0 ${mergedToolbar ? 'mb-2' : `space-y-1 ${isFlatVariant ? (replyCompact ? 'mb-2' : 'mb-3') : 'mb-1.5'}`}`}>
        {mergedToolbar ? (
          <div className="flex max-w-full shrink-0 items-stretch gap-2 overflow-x-auto overflow-y-hidden rounded-t-lg border-b border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-slate-50/40 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] dark:border-[oklch(0.4_0.02_264/0.8)] dark:from-slate-900/90 dark:to-slate-900/50 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:gap-2.5 sm:px-3 sm:py-2">
            <div className="flex min-w-0 max-w-[min(42%,15rem)] shrink-0 items-center gap-1.5 sm:max-w-[16rem]">
              <label
                className="mb-0 flex shrink-0 items-center gap-1 text-[11px] font-medium leading-none"
                style={{
                  color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
                  fontFamily: 'inherit',
                  letterSpacing: '0.1px',
                  fontWeight: 500,
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '10px', color: '#00A8FF' }} />
                To
              </label>
              <input
                type="text"
                title={recipients.to.join(', ')}
                className={`min-w-0 flex-1 max-w-full box-border rounded-md border border-slate-200/80 bg-white/90 py-1.5 text-[11px] transition-colors duration-150 focus:outline-none focus-visible:border-sky-400 focus-visible:ring-2 focus-visible:ring-sky-400/25 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.7)] ${isFlatVariant ? (replyCompact ? 'px-2' : 'px-2.5') : 'px-2 py-1'}`}
                style={{
                  color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
                  fontFamily: 'inherit',
                  letterSpacing: '0.2px',
                  cursor: isExistingThread ? 'not-allowed' : 'text',
                  opacity: isExistingThread ? 0.75 : 1,
                }}
                value={recipients.to.join(', ')}
                disabled={isExistingThread}
                onChange={(e) => {
                  if (!isExistingThread) {
                    hasManuallyEditedToRef.current = true;
                    setRecipients({ ...recipients, to: e.target.value.split(',').map(v => v.trim()) });
                  }
                }}
                placeholder="Recipients…"
                onFocus={(e) => {
                  if (!isExistingThread) {
                    e.currentTarget.style.borderColor = '#00A8FF';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,168,255,0.12)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            {mergedToolbar}
          </div>
        ) : (
        <div className="flex flex-col">
          <label
            className="text-xs font-medium mb-0.5"
            style={{
              color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
              fontFamily: 'inherit',
              letterSpacing: '0.1px',
              fontWeight: 500,
              lineHeight: '1.3'
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-1" style={{ fontSize: '10px', color: '#00A8FF' }} />
            To
          </label>
          <input
            type="text"
            className={`mt-0.5 w-full min-w-0 max-w-full box-border rounded text-xs transition-colors duration-150 focus:outline-none ${isFlatVariant ? (replyCompact ? 'px-2.5 py-1.5' : 'px-3 py-2') : 'px-2 py-1'}`}
            style={{
              backgroundColor: isExistingThread
                ? (isDarkMode ? 'rgba(37, 41, 50, 0.4)' : 'rgba(249, 250, 251, 0.8)')
                : (isDarkMode ? 'rgba(37, 41, 50, 0.6)' : 'rgba(255,255,255,0.5)'),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
              fontFamily: 'inherit',
              letterSpacing: '0.2px',
              cursor: isExistingThread ? 'not-allowed' : 'text',
              opacity: isExistingThread ? 0.7 : 1
            }}
            value={recipients.to.join(', ')}
            disabled={isExistingThread}
            onChange={(e) => {
              if (!isExistingThread) {
                hasManuallyEditedToRef.current = true;
                setRecipients({ ...recipients, to: e.target.value.split(',').map(v => v.trim()) });
              }
            }}
            placeholder="Add recipients (comma separated)"
            onFocus={(e) => {
              if (!isExistingThread) {
                e.currentTarget.style.borderColor = '#00A8FF';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,168,255,0.1)';
              }
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {/* TODO: CC and BCC fields temporarily disabled - not fully implemented yet */}
          {/* <div className="flex items-center gap-2 mt-1">
            <Tooltip content="Add CC recipients (visible to all)" position="top">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hasManuallyToggledCcRef.current = true;
                  setShowCc(prev => !prev);
                }}
                className="px-2.5 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 transition-colors duration-150"
                style={{
                  color: '#00A8FF',
                  backgroundColor: isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)',
                  border: '1px solid rgba(0, 168, 255, 0.2)',
                  fontFamily: 'inherit',
                  letterSpacing: '0.1px',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 168, 255, 0.15)' : 'rgba(0, 168, 255, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)';
                }}
              >
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: '11px' }} />
                + Cc
              </button>
            </Tooltip>
            <Tooltip content="Add BCC recipients (hidden from others)" position="top">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hasManuallyToggledBccRef.current = true;
                  setShowBcc(prev => !prev);
                }}
                className="px-2.5 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 transition-colors duration-150"
                style={{
                  color: '#00A8FF',
                  backgroundColor: isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)',
                  border: '1px solid rgba(0, 168, 255, 0.2)',
                  fontFamily: 'inherit',
                  letterSpacing: '0.1px',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 168, 255, 0.15)' : 'rgba(0, 168, 255, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)';
                }}
              >
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: '11px' }} />
                + Bcc
              </button>
            </Tooltip>
          </div> */}
        </div>
        )}
        {/* Cc / Bcc — minimal toggle + comma-separated text inputs. Reply-all
            pre-fills Cc; the operator can view/edit both here. Manual edits set
            hasManuallyEdited*Ref so the reply-all recompute effect won't clobber them. */}
        <div className={`flex items-center gap-2 ${isFlatVariant ? 'mt-1.5' : 'mt-1'}`}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              hasManuallyToggledCcRef.current = true;
              setShowCc((prev) => !prev);
            }}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-colors duration-150"
            style={{
              color: '#00A8FF',
              backgroundColor: showCc
                ? (isDarkMode ? 'rgba(0, 168, 255, 0.15)' : 'rgba(0, 168, 255, 0.12)')
                : (isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)'),
              border: '1px solid rgba(0, 168, 255, 0.2)',
              fontFamily: 'inherit',
              letterSpacing: '0.1px',
            }}
          >
            <FontAwesomeIcon icon={faCopy} style={{ fontSize: '10px' }} />
            {showCc ? 'Hide Cc' : '+ Cc'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              hasManuallyToggledBccRef.current = true;
              setShowBcc((prev) => !prev);
            }}
            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition-colors duration-150"
            style={{
              color: '#00A8FF',
              backgroundColor: showBcc
                ? (isDarkMode ? 'rgba(0, 168, 255, 0.15)' : 'rgba(0, 168, 255, 0.12)')
                : (isDarkMode ? 'rgba(0, 168, 255, 0.1)' : 'rgba(0, 168, 255, 0.08)'),
              border: '1px solid rgba(0, 168, 255, 0.2)',
              fontFamily: 'inherit',
              letterSpacing: '0.1px',
            }}
          >
            <FontAwesomeIcon icon={faCopy} style={{ fontSize: '10px' }} />
            {showBcc ? 'Hide Bcc' : '+ Bcc'}
          </button>
        </div>
        {showCc && (
          <div className="flex flex-col mt-1">
            <label
              className="text-xs font-medium mb-0.5"
              style={{
                color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
                fontFamily: 'inherit',
                letterSpacing: '0.1px',
                fontWeight: 500,
                lineHeight: '1.3'
              }}
            >
              Cc
            </label>
            <input
              type="text"
              className="rounded px-2 py-1 mt-0.5 text-xs transition-colors duration-150 focus:outline-none"
              style={{
                backgroundColor: isDarkMode ? 'rgba(37, 41, 50, 0.6)' : 'rgba(255,255,255,0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
                fontFamily: 'inherit',
                letterSpacing: '0.1px',
                lineHeight: '1.3'
              }}
              value={recipients.cc.join(', ')}
              onChange={(e) => {
                hasManuallyEditedCcRef.current = true;
                setRecipients({ ...recipients, cc: e.target.value.split(',').map(v => v.trim()) });
              }}
              placeholder="Add Cc (comma separated)"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#00A8FF';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,168,255,0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {showBcc && (
          <div className="flex flex-col mt-1">
            <label
              className="text-xs font-medium mb-0.5"
              style={{
                color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#3A3A3A',
                fontFamily: 'inherit',
                letterSpacing: '0.1px',
                fontWeight: 500,
                lineHeight: '1.3'
              }}
            >
              Bcc
            </label>
            <input
              type="text"
              className="rounded px-2 py-1 mt-0.5 text-xs transition-colors duration-150 focus:outline-none"
              style={{
                backgroundColor: isDarkMode ? 'rgba(37, 41, 50, 0.6)' : 'rgba(255,255,255,0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
                fontFamily: 'inherit',
                letterSpacing: '0.1px',
                lineHeight: '1.3'
              }}
              value={recipients.bcc.join(', ')}
              onChange={(e) => {
                hasManuallyEditedBccRef.current = true;
                setRecipients({ ...recipients, bcc: e.target.value.split(',').map(v => v.trim()) });
              }}
              placeholder="Add Bcc (comma separated)"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#00A8FF';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,168,255,0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
      </div>

      <div
        className={`email-thread-reply-editor-wrap mt-1 w-full min-w-0 ${
          roomyLayout ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-hidden'
        }`}
        onKeyDownCapture={(e) => {
          // Cmd/Ctrl + Enter sends (only when the send button would be enabled).
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isButtonEnabled && !isSending) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder="Write your reply..."
        />
      </div>
      {attachments.length > 0 && (
        <div className={`shrink-0 space-y-1 ${isFlatVariant ? 'mt-3' : 'mt-1.5'}`}>
          {attachments.map((attachment, index) => (
            <FilePreview
              key={index}
              file={attachment.file}
              onRemove={() => removeAttachment(index)}
            />
          ))}
        </div>
      )}
      </div>
      <div
        className={
          documentFlowReply
            ? "mt-1.5 flex min-w-0 w-full max-w-full flex-wrap shrink-0 items-center justify-between gap-2 border-t border-slate-200/90 bg-white px-2 pb-3.5 pt-3 shadow-[0_-4px_14px_rgba(15,23,42,0.07)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-4px_14px_rgba(0,0,0,0.3)] rounded-b-[20px] border-b border-slate-300/90 dark:border-[oklch(0.4_0.02_264)] max-lg:gap-1.5 max-lg:px-1 max-lg:pt-2 max-lg:pb-2"
            : "mt-1.5 flex min-w-0 w-full max-w-full flex-wrap shrink-0 items-center justify-between gap-2 border-t border-slate-200/80 bg-white px-0.5 pb-2 pt-2.5 shadow-[0_-6px_16px_rgba(15,23,42,0.08)] dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_-6px_16px_rgba(0,0,0,0.35)] max-lg:px-0 max-lg:pt-2 max-lg:pb-1.5"
        }
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:flex-initial">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.doc,.docx,.csv,.png,.jpg,.jpeg,.gif,.txt"
          />
          <button
            type="button"
            onClick={() => {
              fileInputRef.current?.click();
            }}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors duration-150"
            style={{
              backgroundColor: isDarkMode ? 'rgba(37, 41, 50, 0.6)' : 'rgba(255,255,255,0.5)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: isDarkMode ? '#E5E7EB' : '#1E1E1E',
              fontFamily: 'inherit',
              letterSpacing: '0.1px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37, 41, 50, 0.7)' : 'rgba(255,255,255,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37, 41, 50, 0.6)' : 'rgba(255,255,255,0.5)';
            }}
          >
            <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: '11px', color: '#00A8FF' }} />
            <span>Attach</span>
          </button>
          {canDictate && (
            <MicButton
              size="sm"
              title="Dictate a reply — AI formalizes the tone"
              onTranscript={handleDictationTranscript}
              onError={showVoiceError}
              disabled={voicePhase === "formalizing" || isSending}
            />
          )}
          {voicePhase === "formalizing" && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10.5px] font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-[9px]" />
              Formalizing…
            </span>
          )}
          {voicePhase === "idle" && lastDictation && (
            <span className="inline-flex min-w-0 shrink items-center gap-1.5 truncate rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-medium text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300">
              ✦ Formalized from dictation
              <button
                type="button"
                onClick={swapToRawDictation}
                className="shrink-0 font-semibold text-blue-600 hover:underline dark:text-blue-400"
              >
                Use raw instead
              </button>
              <button
                type="button"
                onClick={() => setLastDictation(null)}
                aria-label="Dismiss"
                className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                ×
              </button>
            </span>
          )}
          {voiceError && (
            <span className="min-w-0 shrink truncate text-[10.5px] font-medium text-red-500" role="status">
              {voiceError}
            </span>
          )}
        </div>
        {isSending && (
          <div
            aria-hidden
            style={{
              position: "fixed",
              left: "50%",
              bottom: "96px",
              transform: "translateX(-50%)",
              zIndex: 60,
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "13px 20px",
              borderRadius: "14px",
              background: "var(--card-solid, #ffffff)",
              border: "1px solid var(--card-border, #e2e8f0)",
              boxShadow:
                "0 2px 6px rgba(15,23,42,.05), 0 24px 60px -26px rgba(15,23,42,.28)",
              animation: "fkqw-rise .35s ease both",
            }}
          >
            {/* Design motion: an envelope travels a dashed line while sending. */}
            <span style={{ position: "relative", width: "120px", height: "20px", display: "inline-block" }}>
              <span style={{ position: "absolute", top: "50%", left: 0, right: 0, borderTop: "1.5px dashed var(--blue-line, rgba(37,99,235,0.22))" }} />
              <span style={{ position: "absolute", top: "50%", left: 0, transform: "translateY(-50%)", color: "var(--blue, #2563eb)", offsetPath: "path('M 0 10 L 120 10')", animation: "fkqw-send 1.8s ease-in-out infinite" }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: "16px" }} />
              </span>
            </span>
            <span style={{ fontWeight: 700, fontSize: "13px", color: "var(--ink, #1e293b)" }}>
              {`Sending ${String(buttonText || "reply").replace(/^send\s+/i, "").toLowerCase() || "reply"}…`}
            </span>
          </div>
        )}
        <button
          onClick={handleSubmit}
          disabled={!isButtonEnabled || isSending}
          className="flex shrink-0 items-center gap-1.5 rounded px-4 py-2 text-sm font-medium transition-colors duration-150 max-lg:px-2.5 max-lg:py-1.5 max-lg:text-xs max-[960px]:gap-1"
          style={{
            background: isButtonEnabled
              ? (isSending
                ? 'linear-gradient(135deg, rgba(0,176,255,0.75), rgba(0,140,255,0.6))'
                : 'linear-gradient(135deg, rgba(0,176,255,0.85), rgba(0,140,255,0.7))')
              : 'rgba(0,0,0,0.1)',
            backgroundColor: isButtonEnabled
              ? (isSending ? 'rgba(0,176,255,0.75)' : 'rgba(0,176,255,0.85)')
              : 'rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.35)',
            boxShadow: isButtonEnabled
              ? (isSending
                ? '0 2px 8px rgba(0,176,255,0.3)'
                : '0 2px 8px rgba(0,176,255,0.4)')
              : 'none',
            color: isButtonEnabled
              ? '#FFFFFF'
              : (isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'),
            fontSize: '14px',
            fontFamily: 'inherit',
            letterSpacing: '0.1px',
            cursor: isButtonEnabled && !isSending ? 'pointer' : 'not-allowed',
            opacity: isButtonEnabled ? 1 : 0.6,
            position: 'relative'
          }}
        >
          {isSending ? (
            <>
              <svg
                className="animate-spin"
                style={{ width: '12px', height: '12px', marginRight: '4px' }}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  style={{ color: '#FFFFFF' }}
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  style={{ color: '#FFFFFF' }}
                ></path>
              </svg>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '10px', marginRight: '4px' }} />
              {buttonText}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailThread;
